import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🔓 DEV MODE: Mock user for development when auth is disabled
  const DISABLE_AUTH_DEV = false; // Change to false to re-enable auth
  const DEV_CLIENT_ID =
    process.env.REACT_APP_DEV_CLIENT_ID || 'XFU0yB3VAxNkOZWpHADs6mvM2W1';
  const mockUser = {
    uid: DEV_CLIENT_ID,
    email: 'dev@example.com',
    displayName: 'Dev User',
    /** Firebase User compatibility: dev mock is not a real User — expose getIdToken for API calls */
    getIdToken: async () => {
      const t =
        localStorage.getItem('token') ||
        localStorage.getItem('firebaseToken') ||
        localStorage.getItem('idToken');
      if (t && !['null', 'undefined'].includes(String(t).trim().toLowerCase())) return t;
      console.warn(
        '[Auth] Dev mock user: no JWT in localStorage. API routes need a real Firebase token — sign in or set localStorage.token.'
      );
      return '';
    }
  };

  useEffect(() => {
    // In dev mode with auth disabled, use mock user
    if (DISABLE_AUTH_DEV && process.env.NODE_ENV === 'development') {
      console.log('🔓 Using mock user for development');
      setCurrentUser(mockUser);
      setUserProfile({
        client_id: DEV_CLIENT_ID,
        user_id: DEV_CLIENT_ID,
        companyName: 'Dev Company',
        email: 'dev@example.com',
        industry: 'Technology',
        location: 'San Francisco'
      });
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);

        // Fetch user profile from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserProfile(userDoc.data());
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const logout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

